import React, { useEffect, useState } from 'react';
import { Line } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend } from 'chart.js';
import { LockClosedIcon, UserCircleIcon, TrophyIcon, CreditCardIcon, ChartBarIcon, ClockIcon, SparklesIcon } from '@heroicons/react/24/outline';
import { toast } from 'react-toastify';
import { motion } from 'framer-motion';
import MarkdownRenderer from '../components/MarkdownRenderer';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';

const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return '—';
    return d.toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch { return '—'; }
};

const formatDuration = (seconds) => {
  if (seconds === null || seconds === undefined || isNaN(seconds)) return '—';
  if (seconds === 0) return '0 сек';
  const mins = Math.floor(Math.abs(seconds) / 60);
  const secs = Math.floor(Math.abs(seconds) % 60);
  return mins > 0 ? `${mins} мин ${secs} сек` : `${secs} сек`;
};

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

const Profile = () => {
  const { limits, checkAuth } = useAuth();
  const { theme } = useTheme();
  const [user, setUser] = useState({ name: '', email: '', avatar: '', points: 0, status: '', role: '' });
  const [achievements, setAchievements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dialogs, setDialogs] = useState([]);
  const [achievementSearch, setAchievementSearch] = useState('');
  const [selectedAchievement, setSelectedAchievement] = useState(null);
  const [dailyActivity, setDailyActivity] = useState([]);
  const [activityLoading, setActivityLoading] = useState(true);
  const [completedDialogs, setCompletedDialogs] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const [showTopUpModal, setShowTopUpModal] = useState(false);
  const [topUpAmount, setTopUpAmount] = useState(500);
  const [topUpLoading, setTopUpLoading] = useState(false);
  const [selectedDialog, setSelectedDialog] = useState(null);
  const [dialogFilter, setDialogFilter] = useState('all');
  const [tokenHistory, setTokenHistory] = useState([]);
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [showTokenHistory, setShowTokenHistory] = useState(false);
  const [currentTariff, setCurrentTariff] = useState(null);
  const [pendingPayments, setPendingPayments] = useState([]);
  const [availableTariffs, setAvailableTariffs] = useState([]);
  const [organization, setOrganization] = useState(null);
  const [editingProfile, setEditingProfile] = useState(false);
  const [profileForm, setProfileForm] = useState({ username: '', email: '', first_name: '', last_name: '', phone: '', birthday: '' });
  const [tokenRate, setTokenRate] = useState(1000);

  const fetchProfileData = async () => {
    setRefreshing(true); setLoading(true); setError(null); setActivityLoading(true);
    try {
      const token = localStorage.getItem('access_token');
      if (!token) throw new Error('Токен авторизации не найден');
      const profileResponse = await fetch('/api/profile', { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }, credentials: 'include' });
      if (!profileResponse.ok) throw new Error('Не удалось загрузить данные профиля');
      const profileData = await profileResponse.json();
      setUser(profileData.user);
      setProfileForm({ username: profileData.user.username || '', email: profileData.user.email || '', first_name: profileData.user.first_name || '', last_name: profileData.user.last_name || '', phone: profileData.user.phone || '', birthday: profileData.user.birthday || '' });
      setCurrentTariff(profileData.tariff || null);
      setAvailableTariffs(profileData.availableTariffs || []);
      setCompletedDialogs(profileData.statistics.completedDialogs || 0);
      setDailyActivity(profileData.dailyActivity || []);
      setDialogs(profileData.dialogs || []);
      setOrganization(profileData.organization || null);
      const achievementsResponse = await fetch('/api/achievements/user', { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }, credentials: 'include' });
      if (achievementsResponse.ok) setAchievements(await achievementsResponse.json());
      const [usageRes, paymentsRes] = await Promise.all([
        fetch('/api/payments/token-usage', { headers: { Authorization: `Bearer ${token}` } }),
        fetch('/api/payments/history', { headers: { Authorization: `Bearer ${token}` } })
      ]);
      if (usageRes.ok) setTokenHistory(await usageRes.json());
      if (paymentsRes.ok) setPaymentHistory(await paymentsRes.json());
      
      // Загружаем ожидающие платежи за последние 24 часа
      const pendingRes = await fetch('/api/payments/pending', { headers: { Authorization: `Bearer ${token}` } });
      if (pendingRes.ok) setPendingPayments(await pendingRes.json());
    } catch (err) { setError(err.message); } finally { setLoading(false); setRefreshing(false); setActivityLoading(false); }
  };

  useEffect(() => { fetchProfileData(); }, []);

  // Проверка статуса платежа после возврата с ЮКассы
  useEffect(() => {
    const checkPendingPayments = async () => {
      const token = localStorage.getItem('access_token');
      if (!token) return;
      try {
        const res = await fetch('/api/payments/history', { headers: { Authorization: `Bearer ${token}` } });
        if (!res.ok) return;
        const payments = await res.json();
        const pending = payments.filter(p => p.status === 'pending');
        for (const p of pending) {
          const checkRes = await fetch(`/api/payments/check/${p.id}`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } });
          if (checkRes.ok) {
            const data = await checkRes.json();
            if (data.status === 'success') {
              toast.success('Платёж успешно обработан!');
              fetchProfileData();
              checkAuth(); // Обновляем глобальное состояние user/limits
              break;
            }
          }
        }
      } catch (e) { console.error(e); }
    };
    checkPendingPayments();
    // Загрузка курса токенов
    fetch('/api/payments/rate').then(r => r.json()).then(d => setTokenRate(d.tokens_per_ruble || 1000)).catch(() => {});
  }, []);

  const filteredAchievements = achievements.filter(a => a.unlocked && ((a.name || '').toLowerCase().includes(achievementSearch.toLowerCase()) || (a.description || '').toLowerCase().includes(achievementSearch.toLowerCase()))).sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  const totalAchievements = achievements.filter(a => a.unlocked).length;
  const totalPoints = achievements.filter(a => a.unlocked).reduce((sum, a) => sum + (a.points || 0), 0);

  const days = 7;
  const today = new Date();
  const dateLabels = Array.from({ length: days }, (_, i) => { const d = new Date(today); d.setDate(today.getDate() - (days - 1 - i)); return d.toISOString().split('T')[0]; });
  const completedPerDay = dateLabels.map(date => { const activity = dailyActivity.find(item => item.date === date); return activity ? activity.completed_dialogs : 0; });
  let maxStreak = 0, currentStreak = 0, tempStreak = 0;
  for (let i = completedPerDay.length - 1; i >= 0; i--) { if (completedPerDay[i] > 0) { tempStreak++; if (tempStreak > maxStreak) maxStreak = tempStreak; if (i === completedPerDay.length - 1) currentStreak = tempStreak; } else { tempStreak = 0; } }

  if (loading) return (
    <div className="min-h-screen bg-gray-50 dark:bg-slate-900 flex items-center justify-center">
      <div className="text-center"><div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div><p className="text-gray-600 dark:text-white/70">Загрузка профиля...</p></div>
    </div>
  );

  if (error) return (
    <div className="min-h-screen bg-gray-50 dark:bg-slate-900 flex items-center justify-center">
      <div className="bg-red-100 dark:bg-red-500/20 border border-red-300 dark:border-red-500/30 rounded-2xl p-8 text-center"><p className="text-red-600 dark:text-red-400 text-lg">Ошибка: {error}</p></div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-slate-900 py-8 px-4">
      <div className="max-w-5xl mx-auto space-y-6">
        {/* Header */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-3xl p-6 md:p-8 shadow-lg">
          <div className="mb-6">
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white">Мой профиль</h1>
          </div>

          <div className="flex flex-col md:flex-row items-center md:items-start gap-6">
            <div className="relative group">
              <div className="w-24 h-24 md:w-32 md:h-32 rounded-2xl bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-3xl md:text-4xl font-bold text-white shadow-2xl overflow-hidden">
                {user.avatar ? <img src={user.avatar} alt="avatar" className="w-full h-full rounded-2xl object-cover" /> : (user.username || user.email || 'U').slice(0, 2).toUpperCase()}
              </div>
              <label className="absolute inset-0 flex items-center justify-center bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer rounded-2xl">
                <span className="text-white text-xs">Изменить</span>
                <input type="file" accept="image/*" className="hidden" onChange={async (e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  const formData = new FormData();
                  formData.append('file', file);
                  try {
                    const token = localStorage.getItem('access_token');
                    const res = await fetch('/api/auth/avatar', { method: 'POST', headers: { 'Authorization': `Bearer ${token}` }, body: formData });
                    if (res.ok) { const data = await res.json(); setUser(u => ({ ...u, avatar: `/static/avatars/${data.avatar}` })); toast.success('Аватар обновлён'); }
                    else { const err = await res.json(); toast.error(err.error || 'Ошибка загрузки'); }
                  } catch { toast.error('Ошибка загрузки'); }
                }} />
              </label>
              <div className="absolute -bottom-2 -right-2 w-8 h-8 bg-green-500 rounded-full border-4 border-gray-50 dark:border-slate-900"></div>
            </div>

            <div className="flex-1 text-center md:text-left">
              {editingProfile ? (
                <div className="space-y-4 max-w-md">
                  <div className="grid grid-cols-2 gap-3">
                    <input type="text" value={profileForm.first_name} onChange={e => setProfileForm(p => ({ ...p, first_name: e.target.value }))} placeholder="Имя" className="w-full px-4 py-3 bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-xl text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500" />
                    <input type="text" value={profileForm.last_name} onChange={e => setProfileForm(p => ({ ...p, last_name: e.target.value }))} placeholder="Фамилия" className="w-full px-4 py-3 bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-xl text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500" />
                  </div>
                  <input type="text" value={profileForm.username} onChange={e => setProfileForm(p => ({ ...p, username: e.target.value }))} placeholder="Никнейм" className="w-full px-4 py-3 bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-xl text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500" />
                  <input type="email" value={profileForm.email} onChange={e => setProfileForm(p => ({ ...p, email: e.target.value }))} placeholder="Email" className="w-full px-4 py-3 bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-xl text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500" />
                  <input type="tel" value={profileForm.phone} onChange={e => setProfileForm(p => ({ ...p, phone: e.target.value }))} placeholder="Телефон" className="w-full px-4 py-3 bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-xl text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500" />
                  <input type="text" value={profileForm.birthday} onChange={e => setProfileForm(p => ({ ...p, birthday: e.target.value }))} placeholder="Дата рождения" className="w-full px-4 py-3 bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-xl text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500" />
                  <div className="flex gap-3">
                    <button onClick={async () => {
                      try {
                        const token = localStorage.getItem('access_token');
                        const res = await fetch('/api/auth/profile', { method: 'PUT', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }, body: JSON.stringify(profileForm) });
                        if (res.ok) { setUser(await res.json()); setEditingProfile(false); toast.success('Профиль обновлён'); } else { toast.error((await res.json()).error || 'Ошибка'); }
                      } catch { toast.error('Ошибка сохранения'); }
                    }} className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-medium transition">Сохранить</button>
                    <button onClick={() => { setEditingProfile(false); setProfileForm({ username: user.username || '', email: user.email || '', first_name: user.first_name || '', last_name: user.last_name || '', phone: user.phone || '', birthday: user.birthday || '' }); }} className="px-6 py-2 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-900 dark:text-white rounded-xl font-medium transition">Отмена</button>
                  </div>
                </div>
              ) : (
                <>
                  <h2 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white mb-1">{user.first_name || user.last_name ? `${user.first_name || ''} ${user.last_name || ''}`.trim() : (user.username || user.email)}</h2>
                  {user.username && (user.first_name || user.last_name) && <p className="text-gray-500 dark:text-gray-400 text-sm mb-1">@{user.username}</p>}
                  <p className="text-gray-600 dark:text-gray-400 mb-1">{user.email}</p>
                  {user.phone && <p className="text-gray-500 dark:text-gray-400 text-sm mb-1">📱 {user.phone}</p>}
                  {user.birthday && <p className="text-gray-500 dark:text-gray-400 text-sm mb-3">🎂 {user.birthday}</p>}
                  <button onClick={() => setEditingProfile(true)} className="text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 text-sm font-medium transition mb-4">Редактировать профиль</button>
                  <div className="flex flex-wrap gap-2 justify-center md:justify-start">
                    <span className="px-3 py-1 bg-purple-100 dark:bg-purple-500/20 text-purple-700 dark:text-purple-400 rounded-full text-sm font-medium">{user.role?.toUpperCase()}</span>
                    {currentTariff && <span className="px-3 py-1 bg-blue-100 dark:bg-blue-500/20 text-blue-700 dark:text-blue-400 rounded-full text-sm font-medium">{currentTariff.name}</span>}
                  </div>
                </>
              )}
            </div>
          </div>
        </motion.div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { icon: ChartBarIcon, value: completedDialogs, label: 'Тренировок', color: 'blue' },
            { icon: TrophyIcon, value: totalAchievements, label: 'Достижений', color: 'green' },
            { icon: SparklesIcon, value: totalPoints, label: 'Баллов', color: 'yellow' },
            { icon: ClockIcon, value: `${currentStreak}/${maxStreak}`, label: 'Серия', color: 'purple' }
          ].map((stat, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.1 }} className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-2xl p-4 text-center shadow-lg">
              <stat.icon className={`w-8 h-8 mx-auto mb-2 ${stat.color === 'blue' ? 'text-blue-500' : stat.color === 'green' ? 'text-green-500' : stat.color === 'yellow' ? 'text-yellow-500' : 'text-purple-500'}`} />
              <div className={`text-2xl md:text-3xl font-bold ${stat.color === 'blue' ? 'text-blue-600 dark:text-blue-400' : stat.color === 'green' ? 'text-green-600 dark:text-green-400' : stat.color === 'yellow' ? 'text-yellow-600 dark:text-yellow-400' : 'text-purple-600 dark:text-purple-400'}`}>{stat.value}</div>
              <div className="text-gray-600 dark:text-gray-400 text-sm">{stat.label}</div>
            </motion.div>
          ))}
        </div>

        {/* Organization */}
        {organization && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 rounded-2xl p-6 shadow-lg">
            <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2"><UserCircleIcon className="w-6 h-6 text-blue-600 dark:text-blue-400" /> Организация</h3>
            <p className="text-gray-800 dark:text-white/80 font-medium text-lg">{organization.name}</p>
            {organization.moderators?.length > 0 && (
              <div className="mt-4 space-y-2">
                <p className="text-gray-600 dark:text-gray-400 text-sm">Модераторы:</p>
                {organization.moderators.map(mod => <div key={mod.id} className="px-4 py-2 bg-white dark:bg-gray-800 rounded-xl text-gray-800 dark:text-gray-200">{mod.username || mod.email}</div>)}
              </div>
            )}
          </motion.div>
        )}

        {/* Pending Payments */}
        {pendingPayments.length > 0 && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-yellow-50 dark:bg-yellow-900/30 border border-yellow-200 dark:border-yellow-800 rounded-2xl p-6 shadow-lg">
            <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
              <ClockIcon className="w-6 h-6 text-yellow-600 dark:text-yellow-400" /> 
              Ожидающие платежи
            </h3>
            <p className="text-gray-600 dark:text-gray-400 text-sm mb-4">У вас есть неоплаченные платежи за последние 24 часа</p>
            <div className="space-y-3">
              {pendingPayments.map(payment => (
                <div key={payment.id} className="bg-white dark:bg-gray-800 rounded-xl p-4 flex items-center justify-between">
                  <div>
                    <p className="font-medium text-gray-900 dark:text-white">{payment.amount} ₽</p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {payment.tariff_name ? `Тариф: ${payment.tariff_name}` : 'Пополнение токенов'}
                    </p>
                    <p className="text-xs text-gray-400">{formatDate(payment.created_at)}</p>
                  </div>
                  <button
                    onClick={async () => {
                      try {
                        // Получаем ссылку на оплату через API ЮКассы
                        const token = localStorage.getItem('access_token');
                        const res = await fetch(`/api/payments/${payment.id}/continue`, {
                          method: 'POST',
                          headers: { Authorization: `Bearer ${token}` }
                        });
                        if (res.ok) {
                          const data = await res.json();
                          if (data.confirmation_url) {
                            window.open(data.confirmation_url, '_blank');
                          } else {
                            toast.error('Не удалось получить ссылку на оплату');
                          }
                        } else {
                          // Fallback - создаем новый платеж
                          if (payment.tariff_name) {
                            window.location.href = '/tariffs';
                          } else {
                            setTopUpAmount(payment.amount);
                            setShowTopUpModal(true);
                          }
                        }
                      } catch (e) {
                        toast.error('Ошибка получения ссылки на оплату');
                      }
                    }}
                    className="px-4 py-2 bg-yellow-600 hover:bg-yellow-700 text-white rounded-lg text-sm font-medium transition"
                  >
                    Продолжить оплату
                  </button>
                </div>
              ))}
            </div>
          </motion.div>
        )}

        {/* Tokens Section */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-2xl p-6 shadow-lg">
          {/* Заголовок */}
          <div className="flex items-center gap-3 mb-5">
            <div className="w-10 h-10 bg-orange-100 dark:bg-orange-900/30 rounded-xl flex items-center justify-center">
              <CreditCardIcon className="w-5 h-5 text-orange-600 dark:text-orange-400" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-gray-900 dark:text-white">Баланс токенов</h3>
              <p className="text-xs text-gray-500 dark:text-gray-400">Управление балансом и история операций</p>
            </div>
          </div>

          {/* Баланс */}
          <div className="bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-700/50 dark:to-gray-700/30 rounded-xl p-5 mb-5 border border-gray-200 dark:border-gray-600">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <p className="text-gray-500 dark:text-gray-400 text-xs uppercase tracking-wide mb-1">Доступно</p>
                <div className="flex items-baseline gap-2">
                  <span className="text-4xl font-bold text-gray-900 dark:text-white">{limits?.total_available !== null ? limits?.total_available?.toLocaleString() : '∞'}</span>
                  <span className="text-gray-500 dark:text-gray-400 text-sm">токенов</span>
                </div>
              </div>
              <div className="flex gap-2">
                {!organization && (
                  <button onClick={() => setShowTopUpModal(true)} className="flex items-center gap-2 px-4 py-2.5 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition text-sm">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" /></svg>
                    Пополнить
                  </button>
                )}
                <button onClick={() => setShowTokenHistory(!showTokenHistory)} className="flex items-center gap-2 px-4 py-2.5 bg-white dark:bg-gray-600 text-gray-700 dark:text-gray-200 rounded-lg font-medium hover:bg-gray-50 dark:hover:bg-gray-500 transition text-sm border border-gray-200 dark:border-gray-500">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                  {showTokenHistory ? 'Скрыть' : 'История'}
                </button>
              </div>
            </div>
          </div>

          {/* История операций */}
          {showTokenHistory && (
            <div className="mb-5">
              <div className="flex items-center gap-2 mb-3">
                <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" /></svg>
                <span className="text-sm text-gray-500 dark:text-gray-400 font-medium">История операций</span>
              </div>
              <div className="bg-gray-50 dark:bg-gray-700/30 rounded-xl p-3">
                <div className="max-h-52 overflow-y-auto space-y-1">
                  {[...paymentHistory.filter(p => p.status === 'success').map(p => ({
                    id: `p-${p.id}`,
                    type: 'in',
                    amount: p.tokens_amount,
                    label: p.tariff_name || 'Пополнение баланса',
                    date: p.created_at
                  })), ...tokenHistory.map(t => ({
                    id: `t-${t.id}`,
                    type: 'out',
                    amount: t.total,
                    label: t.scenario_name || 'Тренировка',
                    date: t.created_at
                  }))].sort((a, b) => new Date(b.date) - new Date(a.date)).slice(0, 10).map(item => (
                    <div key={item.id} className="flex items-center justify-between p-3 bg-white dark:bg-gray-800 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition">
                      <div className="flex items-center gap-3">
                        <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${item.type === 'in' ? 'bg-green-100 dark:bg-green-900/30' : 'bg-gray-100 dark:bg-gray-700'}`}>
                          {item.type === 'in' 
                            ? <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 11l5-5m0 0l5 5m-5-5v12" /></svg>
                            : <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 13l-5 5m0 0l-5-5m5 5V6" /></svg>
                          }
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-800 dark:text-gray-200">{item.label}</p>
                          <p className="text-xs text-gray-400">{new Date(item.date).toLocaleDateString('ru', { day: 'numeric', month: 'short', year: 'numeric' })}</p>
                        </div>
                      </div>
                      <div className={`text-sm font-semibold ${item.type === 'in' ? 'text-green-600' : 'text-gray-500'}`}>
                        {item.type === 'in' ? '+' : '−'}{item.amount?.toLocaleString()}
                      </div>
                    </div>
                  ))}
                  {paymentHistory.filter(p => p.status === 'success').length === 0 && tokenHistory.length === 0 && (
                    <div className="text-center py-8">
                      <svg className="w-12 h-12 text-gray-300 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" /></svg>
                      <p className="text-gray-400 text-sm">Пока нет операций</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {(currentTariff || availableTariffs.length > 0) && (
            <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
              <h4 className="text-gray-900 dark:text-white font-medium mb-3">Тарифный план</h4>
              {currentTariff && (
                <div className="mb-4 p-4 bg-blue-50 dark:bg-blue-900/30 rounded-xl border border-blue-200 dark:border-blue-800">
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <p className="text-blue-700 dark:text-blue-300 font-bold text-lg">{currentTariff.name}</p>
                      {currentTariff.source === 'organization' && <span className="text-xs bg-blue-200 dark:bg-blue-800 px-2 py-0.5 rounded">от организации</span>}
                    </div>
                    {(user.tariff_expires_at || (currentTariff?.source === 'organization' && organization?.tariff_expires_at)) && (
                      <div className="text-right">
                        <p className="text-xs text-gray-500 dark:text-gray-400">Действует до</p>
                        <p className="text-sm font-medium text-blue-700 dark:text-blue-300">{new Date(currentTariff?.source === 'organization' ? organization?.tariff_expires_at : user.tariff_expires_at).toLocaleDateString('ru')}</p>
                        <p className="text-xs text-blue-600 dark:text-blue-400">
                          {(() => {
                            const expiresAt = currentTariff?.source === 'organization' ? organization?.tariff_expires_at : user.tariff_expires_at;
                            const end = new Date(expiresAt);
                            const now = new Date();
                            end.setHours(0,0,0,0);
                            now.setHours(0,0,0,0);
                            const days = Math.round((end - now) / (1000 * 60 * 60 * 24));
                            return days > 0 ? `осталось ${days} дн.` : 'истёк';
                          })()}
                        </p>
                      </div>
                    )}
                  </div>
                  <ul className="space-y-2 text-sm text-gray-700 dark:text-gray-300 mb-3">
                    <li>• {currentTariff.monthly_limit > 0 ? `${currentTariff.monthly_limit.toLocaleString()} токенов` : 'Безлимитное общение'}</li>
                    {currentTariff.daily_limit > 0 && <li>• До {currentTariff.daily_limit.toLocaleString()} токенов в день</li>}
                  </ul>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    {user.tokens_used_today !== undefined && (
                      <div className="bg-white dark:bg-gray-800 rounded-lg p-3">
                        <p className="text-gray-500 dark:text-gray-400 text-xs">Использовано сегодня</p>
                        <p className="font-bold text-gray-900 dark:text-white">{(user.tokens_used_today || 0).toLocaleString()}</p>
                      </div>
                    )}
                    {user.tokens_used_month !== undefined && (
                      <div className="bg-white dark:bg-gray-800 rounded-lg p-3">
                        <p className="text-gray-500 dark:text-gray-400 text-xs">Использовано за период</p>
                        <p className="font-bold text-gray-900 dark:text-white">{(user.tokens_used_month || 0).toLocaleString()}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}
              {availableTariffs.length > 0 && !organization && (
                <div className="grid sm:grid-cols-2 gap-3">
                  {availableTariffs.map(t => (
                    <div key={t.id} onClick={async () => {
                      if (organization) return;
                      if (t.price_per_month > 0) {
                        try {
                          const token = localStorage.getItem('access_token');
                          const res = await fetch('/api/payments/create', { method: 'POST', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }, body: JSON.stringify({ amount: t.price_per_month, tariff_id: t.id, return_url: window.location.href }) });
                          const data = await res.json();
                          if (data.confirmation_url) window.location.href = data.confirmation_url; else toast.error(data.error || 'Ошибка');
                        } catch { toast.error('Ошибка платежа'); }
                      } else {
                        try {
                          const token = localStorage.getItem('access_token');
                          const res = await fetch('/api/auth/tariffs/select', { method: 'POST', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }, body: JSON.stringify({ tariff_id: t.id }) });
                          if (res.ok) { toast.success('Тариф выбран'); fetchProfileData(); } else toast.error((await res.json()).error || 'Ошибка');
                        } catch { toast.error('Ошибка'); }
                      }
                    }} className="p-4 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-600 hover:border-blue-500 transition cursor-pointer">
                      <p className="text-gray-900 dark:text-white font-medium">{t.name}</p>
                      <p className="text-gray-600 dark:text-gray-400 text-sm mb-2">{t.price_per_month > 0 ? `${t.price_per_month}₽` : 'Бесплатно'}</p>
                      {t.description && <p className="text-gray-500 dark:text-gray-400 text-xs mb-2">{t.description}</p>}
                      <div className="space-y-1">
                        <p className="text-gray-600 dark:text-gray-400 text-xs">• {t.monthly_limit > 0 ? `${t.monthly_limit.toLocaleString()} токенов` : 'Безлимитное общение'}</p>
                        {t.daily_limit > 0 && <p className="text-gray-600 dark:text-gray-400 text-xs">• До {t.daily_limit.toLocaleString()} токенов в день</p>}
                        <p className="text-gray-600 dark:text-gray-400 text-xs">• Срок действия: {t.period_days || 30} дней</p>
                      </div>
                      {!organization && <p className="text-blue-600 dark:text-blue-400 text-xs mt-2 font-medium">Нажмите для выбора →</p>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </motion.div>

        {/* Achievements */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-2xl p-6 shadow-lg">
          <div className="flex items-center gap-3 mb-6"><TrophyIcon className="w-6 h-6 text-green-500" /><h3 className="text-xl font-bold text-gray-900 dark:text-white">Достижения</h3></div>
          
          <input type="text" placeholder="Поиск достижений..." value={achievementSearch} onChange={e => setAchievementSearch(e.target.value)} className="w-full md:w-72 px-4 py-3 bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-xl text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 mb-6" />
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-h-80 overflow-y-auto pr-2">
            {filteredAchievements.length > 0 ? filteredAchievements.map(ach => (
              <motion.div key={ach.id} whileHover={{ scale: 1.03 }} onClick={() => setSelectedAchievement(ach)} className={`p-4 rounded-xl cursor-pointer transition ${ach.unlocked ? 'bg-green-50 dark:bg-green-900/20 border border-green-300 dark:border-green-700' : 'bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600'}`}>
                <div className="w-12 h-12 mx-auto mb-2 flex items-center justify-center">
                  {ach.unlocked && ach.icon_url ? (
                    <img 
                      src={ach.icon_url} 
                      alt="" 
                      className="w-12 h-12 object-contain" 
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'block';
                      }}
                    />
                  ) : null}
                  <LockClosedIcon 
                    className={`w-12 h-12 ${ach.unlocked && ach.icon_url ? 'hidden' : 'block'} text-gray-400 dark:text-gray-500`} 
                  />
                </div>
                <p className={`font-medium text-center text-sm ${ach.unlocked ? 'text-gray-900 dark:text-white' : 'text-gray-500 dark:text-gray-400'}`}>{ach.name}</p>
                <p className={`text-center text-xs mt-1 ${ach.unlocked ? 'text-yellow-600 dark:text-yellow-400' : 'text-yellow-600/50 dark:text-yellow-400/50'}`}>+{ach.points || 0} баллов</p>
              </motion.div>
            )) : <p className="col-span-full text-center text-gray-500 dark:text-gray-400">Нет достижений</p>}
          </div>
        </motion.div>

        {/* Chart */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-2xl p-6 shadow-lg">
          <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">Динамика тренировок</h3>
          <div className="flex gap-4 text-sm mb-4">
            <span className="text-gray-600 dark:text-gray-400">Текущая серия: <span className="text-blue-600 dark:text-blue-400 font-bold">{currentStreak}</span></span>
            <span className="text-gray-600 dark:text-gray-400">Максимум: <span className="text-green-600 dark:text-green-400 font-bold">{maxStreak}</span></span>
          </div>
          <div className="h-64 bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            {activityLoading ? <div className="flex items-center justify-center h-full text-gray-500 dark:text-gray-400">Загрузка...</div> : (
              <Line data={{ labels: dateLabels, datasets: [{ label: 'Тренировки', data: completedPerDay, borderColor: 'rgb(59,130,246)', backgroundColor: 'rgba(59,130,246,0.2)', tension: 0.4, fill: true }] }} options={{ maintainAspectRatio: false, plugins: { legend: { display: false } }, scales: { x: { ticks: { color: '#6b7280' }, grid: { color: 'rgba(107,114,128,0.1)' } }, y: { beginAtZero: true, ticks: { color: '#6b7280', stepSize: 1 }, grid: { color: 'rgba(107,114,128,0.1)' } } } }} />
            )}
          </div>
        </motion.div>

        {/* My Trainings */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-2xl p-6 shadow-lg">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-bold text-gray-900 dark:text-white">Мои тренировки</h3>
            <select value={dialogFilter} onChange={e => setDialogFilter(e.target.value)} className="px-4 py-2 bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-xl text-gray-900 dark:text-white focus:outline-none">
              <option value="all">Все</option>
              <option value="completed">Завершённые</option>
              <option value="archived">В архиве</option>
              <option value="active">Активные</option>
            </select>
          </div>
          <div className="space-y-3 max-h-96 overflow-y-auto">
            {dialogs.filter(d => dialogFilter === 'all' || d.status === dialogFilter).length === 0 ? <p className="text-gray-500 dark:text-gray-400 text-center py-8">Нет тренировок</p> : dialogs.filter(d => dialogFilter === 'all' || d.status === dialogFilter).map(d => (
              <div key={d.id} onClick={() => setSelectedDialog(d)} className="p-4 bg-gray-50 dark:bg-gray-700 rounded-xl cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 transition border border-gray-200 dark:border-gray-600">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-gray-900 dark:text-white font-medium">{d.scenario_name || 'Сценарий'}</p>
                    <p className="text-gray-500 dark:text-gray-400 text-sm">{formatDate(d.started_at)}</p>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${d.status === 'completed' ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400' : d.status === 'archived' ? 'bg-gray-100 dark:bg-gray-900/30 text-gray-700 dark:text-gray-400' : 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400'}`}>
                    {d.status === 'completed' ? 'Завершён' : d.status === 'archived' ? 'В архиве' : 'Активен'}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </motion.div>
      </div>

      {/* Achievement Modal */}
      {selectedAchievement && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setSelectedAchievement(null)}>
          <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} onClick={e => e.stopPropagation()} className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-2xl p-6 max-w-md w-full shadow-xl">
            <div className="flex justify-between items-start mb-4">
              <h3 className="text-xl font-bold text-gray-900 dark:text-white">{selectedAchievement.name}</h3>
              <button onClick={() => setSelectedAchievement(null)} className="text-gray-400 hover:text-gray-600 dark:hover:text-white text-2xl">&times;</button>
            </div>
            <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center">
              {selectedAchievement.icon_url ? (
                <img 
                  src={selectedAchievement.icon_url} 
                  alt="" 
                  className="w-20 h-20 object-contain" 
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'block';
                  }}
                />
              ) : null}
              <div 
                className={`w-20 h-20 bg-gray-100 dark:bg-gray-700 rounded-xl flex items-center justify-center ${selectedAchievement.icon_url ? 'hidden' : 'block'}`}
              >
                <span className="text-2xl">🏆</span>
              </div>
            </div>
            <p className="text-yellow-600 dark:text-yellow-400 font-medium text-center mb-2">+{selectedAchievement.points || 0} баллов</p>
            {selectedAchievement.requirements?.type && selectedAchievement.requirements.type !== 'none' && (
              <p className="text-blue-600 dark:text-blue-400 text-center text-sm mb-2">Требование: {selectedAchievement.requirements.type === 'total_dialogs' ? `завершить ${selectedAchievement.requirements.value} диалогов` : selectedAchievement.requirements.value}</p>
            )}
            <p className="text-gray-600 dark:text-gray-400 text-center">{selectedAchievement.description}</p>
            {selectedAchievement.unlocked && selectedAchievement.achieved_at && <p className="text-green-600 dark:text-green-400 text-center text-sm mt-2">Получено: {new Date(selectedAchievement.achieved_at).toLocaleDateString()}</p>}
            <button onClick={() => setSelectedAchievement(null)} className="w-full mt-4 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-medium transition">Закрыть</button>
          </motion.div>
        </div>
      )}

      {/* Dialog Modal */}
      {selectedDialog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setSelectedDialog(null)}>
          <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} onClick={e => e.stopPropagation()} className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-2xl p-6 max-w-lg w-full max-h-[80vh] overflow-y-auto shadow-xl">
            <div className="flex justify-between items-start mb-4">
              <h3 className="text-xl font-bold text-gray-900 dark:text-white">{selectedDialog.scenario_name}</h3>
              <button onClick={() => setSelectedDialog(null)} className="text-gray-400 hover:text-gray-600 dark:hover:text-white text-2xl">&times;</button>
            </div>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between"><span className="text-gray-600 dark:text-gray-400">Дата:</span><span className="text-gray-900 dark:text-white">{formatDate(selectedDialog.started_at)}</span></div>
              <div className="flex justify-between"><span className="text-gray-600 dark:text-gray-400">Статус:</span><span className={selectedDialog.status === 'completed' ? 'text-green-600 dark:text-green-400' : selectedDialog.status === 'archived' ? 'text-gray-600 dark:text-gray-400' : 'text-yellow-600 dark:text-yellow-400'}>{selectedDialog.status === 'completed' ? 'Завершён' : selectedDialog.status === 'archived' ? 'В архиве' : 'Активен'}</span></div>
              {selectedDialog.duration && <div className="flex justify-between"><span className="text-gray-600 dark:text-gray-400">Длительность:</span><span className="text-gray-900 dark:text-white">{formatDuration(selectedDialog.duration)}</span></div>}
              {selectedDialog.score && <div className="flex justify-between"><span className="text-gray-600 dark:text-gray-400">Оценка:</span><span className="text-gray-900 dark:text-white">{selectedDialog.score}/10</span></div>}
              {selectedDialog.analysis && (
                <div className="mt-4 p-4 bg-gray-50 dark:bg-gray-700 rounded-xl">
                  <p className="text-gray-900 dark:text-white font-medium mb-2">Отчёт:</p>
                  <div className="text-gray-700 dark:text-gray-300 text-sm prose prose-sm dark:prose-invert max-w-none">
                    <MarkdownRenderer variant="report" darkMode={theme === 'dark'}>{selectedDialog.analysis}</MarkdownRenderer>
                  </div>
                </div>
              )}
            </div>
            <button onClick={() => setSelectedDialog(null)} className="w-full mt-4 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-medium transition">Закрыть</button>
          </motion.div>
        </div>
      )}

      {/* Top Up Modal */}
      {showTopUpModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowTopUpModal(false)}>
          <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} onClick={e => e.stopPropagation()} className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-2xl p-6 max-w-sm w-full shadow-xl">
            <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4">Пополнить токены</h3>
            <div className="mb-3 p-3 bg-blue-50 dark:bg-blue-900/30 rounded-xl text-center">
              <p className="text-blue-700 dark:text-blue-300 text-sm font-medium">Курс: 1 ₽ = {tokenRate.toLocaleString()} токенов</p>
            </div>
            <div className="mb-4">
              <label className="block text-gray-600 dark:text-gray-400 text-sm mb-2">Сумма (₽):</label>
              <input type="number" min="100" step="100" value={topUpAmount} onChange={e => setTopUpAmount(Math.max(100, parseInt(e.target.value) || 100))} className="w-full px-4 py-3 bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-xl text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500" />
              <p className="text-green-600 dark:text-green-400 font-medium mt-2">Вы получите: {(topUpAmount * tokenRate).toLocaleString()} токенов</p>
            </div>
            <div className="flex gap-3">
              <button onClick={async () => {
                setTopUpLoading(true);
                try {
                  const token = localStorage.getItem('access_token');
                  const res = await fetch('/api/payments/create', { method: 'POST', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }, body: JSON.stringify({ amount: topUpAmount, return_url: window.location.href }) });
                  const data = await res.json();
                  if (data.confirmation_url) window.location.href = data.confirmation_url; else toast.error(data.error || 'Ошибка');
                } catch { toast.error('Ошибка платежа'); } finally { setTopUpLoading(false); }
              }} disabled={topUpLoading} className="flex-1 py-3 bg-orange-500 hover:bg-orange-600 text-white rounded-xl font-semibold transition disabled:opacity-50">
                {topUpLoading ? 'Загрузка...' : 'Оплатить'}
              </button>
              <button onClick={() => setShowTopUpModal(false)} className="flex-1 py-3 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-800 dark:text-white rounded-xl font-semibold transition">Отмена</button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default Profile;
